import React from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { HardDrive, Files, Crown, ExternalLink, Mail, AtSign, CalendarDays, Clock, Shield, Download } from "lucide-react";
import api, { fileUrl } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { StatCard } from "@/components/dashboard/StatCard";
import { Loading } from "@/components/common/Loading";
import { AdminRefreshButton } from "@/components/admin/AdminRefreshButton";
import StorageTrend from "@/components/admin/StorageTrend";
import { HoverCard, HoverCardTrigger, HoverCardContent } from "@/components/ui/hover-card";

function formatBytes(bytes) {
  const b = Number(bytes) || 0;
  if (b < 1024) return `${b} B`;
  const units = ["KB", "MB", "GB", "TB"];
  let v = b / 1024;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i += 1; }
  return `${v >= 100 ? Math.round(v) : v.toFixed(1)} ${units[i]}`;
}

function fmtDate(d) {
  if (!d) return "—";
  const dt = new Date(d);
  return isNaN(dt.getTime()) ? "—" : dt.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

function downloadCsv(filename, rows) {
  const csv = rows.map((r) => r.map((c) => {
    const s = c == null ? "" : String(c);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  }).join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

const AREA_COLORS = {
  chatter_image: "bg-teal-500", chatter_video: "bg-teal-700", profile_image: "bg-emerald-500",
  profile_video: "bg-indigo-500", pitchdeck: "bg-amber-500", inquiry: "bg-orange-500",
  resource: "bg-sky-500", event: "bg-rose-500", tag_icons: "bg-slate-400", seo_og: "bg-violet-400",
  other_document: "bg-stone-400", other_image: "bg-stone-300", other_media: "bg-stone-500",
  other_archive: "bg-stone-600", other_file: "bg-slate-300",
};

function Breakdown({ title, rows, total, colorFor, testIdPrefix, action }) {
  return (
    <Card className="border-0 p-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-[640] text-foreground">{title}</h2>
        {action}
      </div>
      <div className="mt-4 space-y-3">
        {rows.length === 0 && (
          <p className="text-sm text-muted-foreground">No files yet.</p>
        )}
        {rows.map((r) => {
          const pct = total ? Math.round((r.bytes / total) * 100) : 0;
          return (
            <div key={r.key} data-testid={`${testIdPrefix}-${r.key}`}>
              <div className="flex items-center justify-between text-sm">
                <span className="truncate pr-3 text-foreground/80">{r.label}</span>
                <span className="shrink-0 font-[560] text-foreground">{formatBytes(r.bytes)} <span className="font-normal text-muted-foreground">· {r.files} {r.files === 1 ? "file" : "files"}</span></span>
              </div>
              <div className="mt-1.5 h-2 w-full overflow-hidden rounded-full bg-secondary">
                <div className={`h-full rounded-full ${colorFor(r.key)}`} style={{ width: `${Math.max(pct, r.bytes > 0 ? 2 : 0)}%` }} />
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

function UserCell({ u }) {
  const avatar = u.avatar_url ? fileUrl(u.avatar_url) : null;
  const initials = (u.name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();
  return (
    <HoverCard openDelay={120} closeDelay={80}>
      <HoverCardTrigger asChild>
        <Link
          to={`/dashboard/admin/users/${u.user_id}`}
          className="inline-flex items-center gap-2.5 font-[560] text-foreground hover:text-primary hover:underline"
          data-testid={`top-user-name-${u.user_id}`}
        >
          <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-full bg-teal-50 text-xs font-[640] text-primary">
            {avatar ? <img src={avatar} alt="" className="h-full w-full object-cover" /> : initials}
          </span>
          <span className="truncate">{u.name}</span>
        </Link>
      </HoverCardTrigger>
      <HoverCardContent align="start" className="w-80" data-testid={`top-user-hovercard-${u.user_id}`}>
        <div className="flex items-start gap-3">
          <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-full bg-teal-50 text-sm font-[640] text-primary">
            {avatar ? <img src={avatar} alt="" className="h-full w-full object-cover" /> : initials}
          </span>
          <div className="min-w-0">
            <p className="truncate font-[620] text-foreground">{u.name}</p>
            {u.handle && <p className="truncate text-xs text-muted-foreground">@{u.handle}</p>}
            <div className="mt-1 flex flex-wrap gap-1">
              {u.role && u.role !== "user" && (
                <span className="inline-flex items-center gap-1 rounded-full bg-teal-50 px-2 py-0.5 text-[11px] font-[560] text-teal-700"><Shield className="h-3 w-3" />{u.role}</span>
              )}
              {u.tier_label && <span className="rounded-full bg-secondary px-2 py-0.5 text-[11px] font-[560] text-foreground/70">{u.tier_label}</span>}
              {u.status && <span className={`rounded-full px-2 py-0.5 text-[11px] font-[560] ${u.status === "active" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>{u.status}</span>}
            </div>
          </div>
        </div>
        <div className="mt-3 space-y-1.5 text-xs text-foreground/80">
          {u.email && <p className="flex items-center gap-2 truncate"><Mail className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />{u.email}</p>}
          <p className="flex items-center gap-2"><AtSign className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />ID: {u.user_id}</p>
          <p className="flex items-center gap-2"><CalendarDays className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />Joined {fmtDate(u.created_at)}</p>
          <p className="flex items-center gap-2"><Clock className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />Last active {fmtDate(u.last_active_at)}</p>
          <p className="flex items-center gap-2 font-[560] text-foreground"><HardDrive className="h-3.5 w-3.5 shrink-0 text-primary" />{formatBytes(u.bytes)} across {u.files} {u.files === 1 ? "file" : "files"}</p>
        </div>
        <Link to={`/dashboard/admin/users/${u.user_id}`} className="mt-3 inline-flex items-center gap-1.5 text-xs font-[560] text-primary hover:underline">
          View full profile <ExternalLink className="h-3 w-3" />
        </Link>
      </HoverCardContent>
    </HoverCard>
  );
}

export default function AdminStorage() {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["admin-storage"],
    queryFn: async () => (await api.get("/admin/storage")).data,
  });
  if (isLoading || !data) return <Loading />;
  const { total, by_area, by_content, top_users } = data;
  const topUser = top_users[0];
  const today = new Date().toISOString().slice(0, 10);

  const exportUsers = () => {
    const rows = [["Rank", "Name", "Email", "Handle", "User ID", "Role", "Tier", "Status", "Joined", "Last active", "Files", "Bytes", "Storage", "Share %"]];
    top_users.forEach((u, i) => rows.push([
      i + 1, u.name, u.email || "", u.handle || "", u.user_id, u.role || "", u.tier_label || "", u.status || "",
      fmtDate(u.created_at), fmtDate(u.last_active_at), u.files, u.bytes, formatBytes(u.bytes),
      total.bytes ? Math.round((u.bytes / total.bytes) * 100) : 0,
    ]));
    downloadCsv(`storage-top-users-${today}.csv`, rows);
  };

  const exportBreakdown = () => {
    const rows = [["Section", "Category", "Bytes", "Storage", "Files", "Share %"]];
    by_area.forEach((r) => rows.push(["Area", r.label, r.bytes, formatBytes(r.bytes), r.files, total.bytes ? Math.round((r.bytes / total.bytes) * 100) : 0]));
    by_content.forEach((r) => rows.push(["Content type", r.label, r.bytes, formatBytes(r.bytes), r.files, total.bytes ? Math.round((r.bytes / total.bytes) * 100) : 0]));
    downloadCsv(`storage-breakdown-${today}.csv`, rows);
  };

  return (
    <div data-testid="admin-storage-page">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-muted-foreground">Platform-wide upload usage. Counts active (non-deleted) files only.</p>
        <div className="flex items-center gap-2">
          <Button variant="secondary" size="sm" onClick={exportUsers} data-testid="storage-export-users">
            <Download className="mr-1.5 h-4 w-4" /> Export users (CSV)
          </Button>
          <AdminRefreshButton testId="storage-refresh" onRefresh={() => queryClient.invalidateQueries({ queryKey: ["admin-storage"] })} />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-3">
        <StatCard icon={HardDrive} label="Total storage used" value={formatBytes(total.bytes)} testId="admin-storage-total" />
        <StatCard icon={Files} label="Total files" value={total.files.toLocaleString()} testId="admin-storage-files" />
        <StatCard icon={Crown} label="Largest user" value={topUser ? formatBytes(topUser.bytes) : "—"} sub={topUser ? topUser.name : undefined} testId="admin-storage-topuser" />
      </div>

      <StorageTrend />

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Breakdown
          title="Storage by area"
          rows={by_area}
          total={total.bytes}
          colorFor={(k) => AREA_COLORS[k] || "bg-slate-300"}
          testIdPrefix="storage-area"
          action={
            <Button variant="ghost" size="sm" onClick={exportBreakdown} data-testid="storage-export-breakdown" className="h-8 text-xs text-muted-foreground hover:text-foreground">
              <Download className="mr-1.5 h-3.5 w-3.5" /> CSV
            </Button>
          }
        />
        <Breakdown title="By content type" rows={by_content} total={total.bytes} colorFor={() => "bg-primary"} testIdPrefix="storage-content" />
      </div>

      <Card className="mt-6 border-0 p-6">
        <h2 className="text-lg font-[640] text-foreground">Top storage users</h2>
        <p className="mt-1 text-sm text-muted-foreground">Hover a name for full details. {top_users.length === 100 ? "Showing top 100." : `Showing top ${top_users.length}.`}</p>
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm" data-testid="top-users-table">
            <thead>
              <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
                <th className="py-2 pr-3 font-[560]">#</th>
                <th className="py-2 pr-3 font-[560]">Member</th>
                <th className="py-2 pr-3 font-[560]">Tier</th>
                <th className="py-2 pr-3 text-right font-[560]">Files</th>
                <th className="py-2 pr-3 text-right font-[560]">Storage</th>
                <th className="py-2 text-right font-[560]">Share</th>
              </tr>
            </thead>
            <tbody>
              {top_users.length === 0 && (
                <tr><td colSpan={6} className="py-6 text-center text-muted-foreground">No uploads on the platform yet.</td></tr>
              )}
              {top_users.map((u, i) => {
                const pct = total.bytes ? Math.round((u.bytes / total.bytes) * 100) : 0;
                return (
                  <tr key={u.user_id} className="border-b border-border/60 last:border-0" data-testid={`top-user-row-${u.user_id}`}>
                    <td className="py-2.5 pr-3 text-muted-foreground">{i + 1}</td>
                    <td className="py-2.5 pr-3"><UserCell u={u} /></td>
                    <td className="py-2.5 pr-3 text-foreground/70">{u.tier_label || "—"}</td>
                    <td className="py-2.5 pr-3 text-right tabular-nums text-foreground/80">{u.files}</td>
                    <td className="py-2.5 pr-3 text-right font-[560] tabular-nums text-foreground">{formatBytes(u.bytes)}</td>
                    <td className="py-2.5 text-right tabular-nums text-muted-foreground">{pct}%</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
